import type { ReactNode } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { BriefcaseIcon, CakeIcon, ContactIcon } from "lucide-react";
import { parseISO } from "date-fns";
import { formatDateMedium, formatWeekday } from "@/lib/format";
import type { ParentBirthdayEntry, UpcomingBirthdaysResponse } from "@/hooks/useBirthdays";

interface UpcomingBirthdaysProps {
  data: UpcomingBirthdaysResponse | undefined;
  /** The window the data was read for (the Dashboard asks for 30 days, #2175 C7). */
  days?: number;
}

const ROW_CLASS =
  "flex items-center gap-2 text-sm w-full text-left hover:bg-muted/50 rounded px-1 py-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const HEADING_CLASS =
  "text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5";

function Section({ heading, children }: { heading: ReactNode; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className={HEADING_CLASS}>{heading}</h4>
      {children}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted-foreground pl-1">{children}</p>;
}

/**
 * The birthday part of the dashboard's Upcoming card: children, then the
 * children's parents, then employees. The same order the employee app's
 * Birthdays card uses inside each day. Each block lists its birthdays soonest
 * first, in the server's order. A parent row names the child(ren) the parent
 * belongs to, because the team knows the parent as "Olivia's mother".
 */
export function UpcomingBirthdays({ data, days = 30 }: UpcomingBirthdaysProps) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const none = i18n._({
    id: "dashboard.upcoming.noBirthdaysInDays",
    message: "No birthdays in the next {days} days",
    values: { days },
  });

  const children = data?.children ?? [];
  const parents = data?.parents ?? [];
  const employees = data?.employees ?? [];

  const when = (date: string, isToday: boolean): string =>
    isToday
      ? i18n._({ id: "dashboard.upcoming.today", message: "today" })
      : i18n._({
          id: "dashboard.dayLabel",
          message: "{weekday}, {date}",
          values: {
            weekday: formatWeekday(parseISO(date), locale, "short"),
            date: formatDateMedium(parseISO(date), locale),
          },
        });

  const turns = (age: number): string =>
    i18n._({ id: "dashboard.upcoming.turnsAge", message: "turns {age}", values: { age } });

  const parentOf = (entry: ParentBirthdayEntry): string => {
    const names = entry.children
      .map((child) => [child.firstname, child.lastname].filter(Boolean).join(" "))
      .join(", ");
    switch (entry.gender) {
      case "FEMALE":
        return i18n._({
          id: "dashboard.upcoming.motherOf",
          message: "mother of {children}",
          values: { children: names },
        });
      case "MALE":
        return i18n._({
          id: "dashboard.upcoming.fatherOf",
          message: "father of {children}",
          values: { children: names },
        });
      default:
        return i18n._({
          id: "dashboard.upcoming.parentOf",
          message: "parent of {children}",
          values: { children: names },
        });
    }
  };

  return (
    <>
      <Section
        heading={
          <>
            <CakeIcon className="h-3 w-3" aria-hidden="true" />
            <Trans id="dashboard.upcoming.childBirthdays">Children Birthdays</Trans>
          </>
        }
      >
        {children.length > 0 ? (
          children.map((b) => (
            <Link key={b.id} to={`/children/${b.id}/edit`} className={ROW_CLASS}>
              <CakeIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" aria-hidden="true" />
              <span className="font-medium">{`${b.firstname} ${b.lastname}`}</span>
              <span className="text-muted-foreground ml-auto flex-shrink-0">
                {when(b.date, b.is_today)}
                {" · "}
                {turns(b.age)}
              </span>
            </Link>
          ))
        ) : (
          <Empty>
            {none}
          </Empty>
        )}
      </Section>

      <Section
        heading={
          <>
            <ContactIcon className="h-3 w-3" aria-hidden="true" />
            <Trans id="dashboard.upcoming.parentBirthdays">Parent Birthdays</Trans>
          </>
        }
      >
        {parents.length > 0 ? (
          parents.map((p) => (
            <Link
              key={p.contact_id}
              to={`/contacts/${p.contact_id}/edit`}
              className={ROW_CLASS}
              data-testid={`parent-birthday-${p.contact_id}`}
            >
              <CakeIcon className="h-3.5 w-3.5 text-primary flex-shrink-0" aria-hidden="true" />
              <span className="font-medium flex-shrink-0">{`${p.firstname} ${p.lastname}`}</span>
              <span className="text-muted-foreground truncate min-w-0">{parentOf(p)}</span>
              <span className="text-muted-foreground ml-auto flex-shrink-0">{when(p.date, p.is_today)}</span>
            </Link>
          ))
        ) : (
          <Empty>
            {none}
          </Empty>
        )}
      </Section>

      <Section
        heading={
          <>
            <BriefcaseIcon className="h-3 w-3" aria-hidden="true" />
            <Trans id="dashboard.upcoming.employeeBirthdays">Employee Birthdays</Trans>
          </>
        }
      >
        {employees.length > 0 ? (
          employees.map((b) => (
            <Link key={b.id} to={`/employees/${b.id}/edit`} className={ROW_CLASS}>
              <CakeIcon className="h-3.5 w-3.5 text-warning-on-surface flex-shrink-0" aria-hidden="true" />
              <span className="font-medium">{`${b.firstname} ${b.lastname}`}</span>
              <span className="text-muted-foreground ml-auto flex-shrink-0">
                {when(b.date, b.is_today)}
                {" · "}
                {turns(b.age)}
              </span>
            </Link>
          ))
        ) : (
          <Empty>
            {none}
          </Empty>
        )}
      </Section>
    </>
  );
}
